import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "./supabaseClient";
import { Loader2 } from "lucide-react";
import { User, Session, AuthError } from "@supabase/supabase-js";
import { setSentryUser, clearSentryUser } from "./sentry";
import { setApiAccessToken } from "./api";

// Wir definieren, wie unser Context aussieht
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  /** E-Mail/Passwort (z. B. Recruiter-Login auf hire.) */
  signInWithEmailPassword: (email: string, password: string) => Promise<{ error?: Error }>;
  /** Magic Link (z. B. Recruiter-Login auf hire.) */
  signInWithMagicLink: (email: string) => Promise<{ error?: Error }>;
}

// Erstellen des Contexts
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  /** Wenn true, wird der Beta-Zugangs-Check (beta_access) übersprungen (z. B. hire-Subdomain). */
  skipBetaCheck?: boolean;
  /** Redirect-Pfad nach Logout (z. B. "/" für hire). */
  logoutRedirectTo?: string;
}

export const AuthProvider = ({
  children,
  skipBetaCheck = false,
  logoutRedirectTo = "/login",
}: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Diese Funktion läuft jetzt im Hintergrund und blockiert nicht mehr den Start
  const checkAccessInBackground = async (sessionData: Session) => {
    if (skipBetaCheck) return;
    const currentUser = sessionData?.user;
    if (!currentUser || !currentUser.email) return;

    const userEmail = currentUser.email.toLowerCase();

    try {
      // 🔍 DB-CHECK mit Timeout
      const dbPromise = supabase
        .from("beta_access")
        .select("email")
        .eq("email", userEmail)
        .maybeSingle();

      const timeoutPromise = new Promise<{ data: null; error: AuthError }>((_, reject) =>
        setTimeout(() => reject(new Error("DB Timeout")), 5000)
      );

      // Typ-sicheres Race
      // Wir casten das timeoutPromise Ergebnis, damit es kompatibel ist
      const result = await Promise.race([dbPromise, timeoutPromise]).catch((err) => ({
        data: null,
        error: err,
      }));

      // Fehlerbehandlung
      if (result.error) {
        if ((result.error as Error).message === "DB Timeout") {
          console.warn("[Auth] DB Timeout - Skip Check");
          return;
        }
        console.error("[Auth] Echter DB-Fehler:", result.error);
        return;
      }

      const { data } = result;

      // Wenn data existiert -> Zugriff erlaubt.
      const isAllowed = !!data;

      if (isAllowed) {
      } else {
        console.warn(`[Auth] ⛔ Zugriff verweigert (Nicht in Beta-Liste): ${userEmail}`);
        alert("Zugriff verweigert: Deine E-Mail ist nicht für die Beta freigeschaltet.");
        await logout(); // Rauswerfen
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.warn("[Auth] DB-Check übersprungen oder Timeout (User bleibt eingeloggt):", message);
    }
  };

  useEffect(() => {
    let mounted = true;
    let initialAuthResolved = false;
    const initTimeout = setTimeout(() => {
      if (mounted && !initialAuthResolved) {
        console.warn("[Auth] INITIAL_SESSION timeout - continuing without blocking UI");
        setLoading(false);
      }
    }, 1500);

    // Listener für Login/Logout Änderungen
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {

      if (mounted) {
        initialAuthResolved = true;
        clearTimeout(initTimeout);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        setApiAccessToken(session?.access_token ?? null);

        // Keep Sentry user context in sync so every error is attributed
        // to the right account — critical for B2B triage.
        if (session?.user) {
          setSentryUser({ id: session.user.id, email: session.user.email });
          checkAccessInBackground(session);
        } else {
          clearSentryUser();
        }
      }
    });

    return () => {
      mounted = false;
      clearTimeout(initTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const loginWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unbekannter Fehler";
      alert("Fehler beim Login: " + message);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      window.location.href = logoutRedirectTo;
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const signInWithEmailPassword = async (
    email: string,
    password: string
  ): Promise<{ error?: Error }> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: new Error(error.message) };
      return {};
    } catch (e) {
      return { error: e instanceof Error ? e : new Error(String(e)) };
    }
  };

  const signInWithMagicLink = async (email: string): Promise<{ error?: Error }> => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) return { error: new Error(error.message) };
      return {};
    } catch (e) {
      return { error: e instanceof Error ? e : new Error(String(e)) };
    }
  };

  // Wir zeigen den Lader nur ganz kurz am Anfang
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-screen bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-4" />
        <p className="text-slate-500 text-sm font-medium animate-pulse">Starte App...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        loginWithGoogle,
        logout,
        signInWithEmailPassword,
        signInWithMagicLink,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook mit Type Guard
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
